import React from 'react'
import { api } from '../api'
import { useCachedFetch } from '../lib/swr'
import WalletIcon from './WalletIcon'
import PastilleCompte from './PastilleCompte'
import { useTheme } from '../theme'
import { useAuth } from '../AuthContext'
import { useCasquette } from '../contexts/casquette'
import { useActionsPortail } from '../contexts/navigation'
import { MVP } from '../mvp'

/**
 * En-tete du hub portail.
 * Web2 : style editorial conforme a docs/manrina-app-complete.jsx
 *   - salutation creole + wordmark serif « lyannaj »
 *   - pastille wallet (solde du scope actif) + pastille de profil a droite
 * Web3 : conserve l'en-tete sombre existant.
 * v7.2.5 : `backChip` = { label, onClick } — le RETOUR vit dans le SLOT GAUCHE
 * de la ligne du header (a la place du wordmark), meme patron que « ← Lyannaj »
 * des modules. Une seule regle partout (sous-pages Compte, Activite).
 */
function HubHeader({ backChip }) {
  // Autonome (S48) : seule reste la prop backChip (fournie par PageHub —
  // chaque page/route declare son retour) — tout le reste vient des contextes.
  const { isAuthenticated, user } = useAuth()
  const { hubMode, isProducer } = useCasquette()
  const { changerCasquette: onModeChange, navigate: onNavigate, setHubTab: onTabChange } = useActionsPortail()
  const { isWeb3 } = useTheme()

  // Pastille wallet (proto v7.2.1) : solde du scope = casquette active, ouvre la
  // page Wallet du Compte. WalletIcon SVG obligatoire (jamais l'emoji).
  const { data: wallet } = useCachedFetch('wallet/me', api.getWallet, { enabled: !!isAuthenticated && MVP.WALLET })
  const solde = Number(hubMode === 'producer' ? wallet?.solde?.producteur : wallet?.solde?.client) || 0
  const soldeTxt = (solde < 0 ? '−' : '') + Math.abs(solde).toFixed(2).replace('.', ',') + '€'
  const openWallet = () => onTabChange?.('wallet')

  // -------- Web3 (theme sombre) : inchange --------
  if (isWeb3) {
    return (
      <div
        className="sticky top-0 z-40 px-4 pb-2 backdrop-blur-xl bg-[#0a0a0f]/90"
        style={{ paddingTop: 'max(1rem, calc(env(safe-area-inset-top) + 0.5rem))' }}
      >
        <div className="flex justify-between items-center mb-1">
          {backChip ? (
            <button
              onClick={backChip.onClick}
              className="rounded-full px-3.5 py-2 text-[12.5px] font-bold text-white border border-white/15 bg-white/10 inline-flex items-center gap-1.5"
            >
              ← <span className="font-serif">{backChip.label}</span>
            </button>
          ) : (
          <div>
            <h1 className="text-xl font-bold text-white">
              {hubMode === 'producer'
                ? `🌱 Bonjour ${user?.prenom || ''}`
                : isAuthenticated && user?.prenom ? `👋 Bonjour ${user.prenom}` : '👋 Bienvenue'}
            </h1>
            <p className="text-[11px] text-white/60">
              Martinique · {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
          </div>
          )}
          <div className="flex items-center gap-2">
            {isAuthenticated && MVP.WALLET && (
            <button
              onClick={openWallet}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/15 bg-white/10 text-xs font-bold text-white"
            >
              <WalletIcon size={14} color="rgb(var(--c-creme))" /> {soldeTxt}
            </button>
            )}
            <PastilleCompte />
          </div>
        </div>
      </div>
    )
  }

  // -------- Web2 (theme clair, prototype) --------
  // px-4 (16 px) : la meme gouttiere que les modules plein ecran, que le contenu
  // du hub en dessous, et que le prototype (`padding: "16px 16px 8px"`). Le
  // header etait en px-5 : la pastille de retour se decalait de 4 px en passant
  // du hub a un module — decalage remonte par Enzo en S33.
  return (
    <div
      className="px-4 pb-2 relative z-10"
      style={{ paddingTop: 'max(1.1rem, calc(env(safe-area-inset-top) + 0.5rem))' }}
    >
      <div className="flex justify-between items-start">
        {backChip ? (
          <button
            onClick={backChip.onClick}
            className="glass-surface rounded-full px-3.5 py-2 text-[12.5px] font-bold text-ink inline-flex items-center gap-1.5 flex-shrink-0 self-center"
          >
            ← <span className="font-serif">{backChip.label}</span>
          </button>
        ) : (
        <div className="min-w-0">
          <p className="text-[11.5px] text-ink/55">
            {isAuthenticated && user?.prenom ? `Bonjou ${user.prenom} 👋` : 'Bonjou 👋'}
          </p>
          <p className="font-serif text-[26px] leading-none text-ink mt-0.5">lyannaj</p>
        </div>
        )}

        <div className="flex items-center gap-2 flex-shrink-0">
          {isAuthenticated && MVP.WALLET && (
          <button
            onClick={openWallet}
            className="glass-surface rounded-full px-3 py-2 flex items-center gap-1.5 text-[11.5px] font-bold text-ink"
          >
            <WalletIcon size={14} /> {soldeTxt}
          </button>
          )}
          {/* Pastille de droite : visiteur / client simple / casquette — UN composant (S62) */}
          <PastilleCompte />
        </div>
      </div>
    </div>
  )
}

export default HubHeader
